'use client';

import { motion } from 'framer-motion';
import { Check, CheckCircle, Play, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Image from 'next/image';
import { useState, useCallback, useMemo } from 'react';

export default function VillaHoya() {
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  // Array of random YouTube video IDs for vacation/luxury travel content
  const videoIds = [
    'dQw4w9WgXcQ', // Rick Astley - Never Gonna Give You Up (classic)
    'jNQXAC9IVRw', // Me at the zoo (first YouTube video)
    'kJQP7kiw5Fk', // Despacito
    '9bZkp7q19f0', // PSY - GANGNAM STYLE
    'YQHsXMglC9A', // Hello - Adele
    'fJ9rUzIMcZQ', // Queen - Bohemian Rhapsody
    'L_jWHffIx5E', // Smells Like Teen Spirit - Nirvana
    'hTWKbfoikeg', // Smells Like Teen Spirit - Nirvana
    'JGwWNGJdvx8', // Shape of You - Ed Sheeran
    'RgKAFK5djSk'  // Wiz Khalifa - See You Again
  ];

  const getRandomVideoId = () => {
    return videoIds[Math.floor(Math.random() * videoIds.length)];
  };

  const [currentVideoId, setCurrentVideoId] = useState(videoIds[0]);

  const handlePlayClick = () => {
    setCurrentVideoId("PzaJPvwHwfY");
    setIsVideoOpen(true);
  };

  const handleCloseVideo = () => {
    setIsVideoOpen(false);
  };

  return (
    <section className="py-20 bg-white relative overflow-hidden">
      {/* Wavy pattern divider */}
      <div className="absolute inset-0 opacity-5">
        <svg className="w-full h-full" viewBox="0 0 1200 200" xmlns="http://www.w3.org/2000/svg">
          <path d="M0,100 C300,50 600,150 900,100 C1050,75 1200,125 1200,100 L1200,200 L0,200 Z" fill="#6B7280" />
        </svg>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Top Section - Hero/Information Block */}
        <div className="grid lg:grid-cols-2 gap-20 items-center mb-20">
          {/* Left Column - Visuals */}
          <div className="relative">
            {/* Main Image */}
            <div className="relative mb-8">
              <div className="relative w-full h-[400px] overflow-hidden border-2 border-secondary p-2">
                <Image
                  src="https://images.pexels.com/photos/823696/pexels-photo-823696.jpeg"
                  alt="Overwater Bungalows in Tropical Setting"
                  fill
                  className="object-cover p-2"
                />
                {/* Play Button Overlay */}
                <div
                  className="absolute top-4 left-4 bg-white rounded-full p-3 shadow-lg cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={handlePlayClick}
                >
                  <Play className="text-primary" size={20} />
                </div>
              </div>
            </div>

            {/* Secondary Image */}
            <div className="absolute bottom-0 -right-10 -mt-16 ml-16 bg-white p-2">
              <div className="w-[200px] h-[200px] overflow-hidden m-2">
                <Image
                  src="https://images.pexels.com/photos/8319460/pexels-photo-8319460.jpeg"
                  alt="Modern Villa with Swimming Pool"
                  fill
                  className="object-cover p-2"
                />
              </div>
            </div>
          </div>

          {/* Right Column - Text Content */}
          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-primary font-medium text-sm uppercase tracking-wider">
                Welcome to 18Bricks Holiday Homes
              </p>
              <h2 className="text-3xl lg:text-4xl font-semibold text-gray-800">
                A Luxury Vacation Rentals Worldwide
              </h2>
            </motion.div>

            <motion.p
              className="text-gray-600 text-lg leading-relaxed"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Experience the epitome of luxury and comfort with our handpicked collection of premium vacation rentals.
              From pristine beaches to bustling cityscapes, we offer exceptional accommodations that transform your
              travel dreams into unforgettable memories.
            </motion.p>

            {/* Key Features */}
            <motion.div
              className="space-y-4"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="flex items-start gap-3">
                <div className="rounded-full p-1 mt-1">
                  <CheckCircle className="text-secondary" size={16} />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800">20 Years of Experience</h4>
                  <p className="text-gray-600 text-sm">Two decades of excellence in luxury hospitality and guest satisfaction.</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="rounded-full p-1 mt-1">
                  <CheckCircle className="text-secondary" size={16} />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800">180+ Destinations</h4>
                  <p className="text-gray-600 text-sm">Explore the world with our extensive network of premium locations.</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <Button>
                Discover More
              </Button>
            </motion.div>
          </div>
        </div>

        {/* Bottom Section - Statistics/Achievements Block */}
        {/* <motion.div
          className="border border-secondary rounded-2xl p-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="grid lg:grid-cols-2 gap-12 items-center">
           
            <div>
              <h3 className="text-3xl font-medium text-gray-800 mb-4">
                We make{' '}
                <span className="relative">
                  vacations and getaways better

                </span>
              </h3>
            </div>

            
            <div className="grid grid-cols-3 gap-8">
              <motion.div
                className="text-center p-6 rounded-xl bg-gradient-to-br from-primary/5 to-secondary/5 hover:from-primary/10 hover:to-secondary/10 transition-all duration-300 cursor-pointer group"
                whileHover={{ scale: 1.05, y: -5 }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <motion.div
                  className="text-3xl font-medium text-gray-800 mb-2 group-hover:text-primary transition-colors duration-300"
                  whileHover={{ scale: 1.1 }}
                >
                  589+
                </motion.div>
                <div className="text-gray-600 font-medium group-hover:text-gray-800 transition-colors duration-300">
                  Luxury Villas
                </div>
              </motion.div>

              <motion.div
                className="text-center p-6 rounded-xl bg-gradient-to-br from-primary/5 to-secondary/5 hover:from-primary/10 hover:to-secondary/10 transition-all duration-300 cursor-pointer group"
                whileHover={{ scale: 1.05, y: -5 }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <motion.div
                  className="text-3xl font-medium text-gray-800 mb-2 group-hover:text-secondary transition-colors duration-300"
                  whileHover={{ scale: 1.1 }}
                >
                  136K
                </motion.div>
                <div className="text-gray-600 font-medium group-hover:text-gray-800 transition-colors duration-300">
                  International Guests
                </div>
              </motion.div>

              <motion.div
                className="text-center p-6 rounded-xl bg-gradient-to-br from-primary/5 to-secondary/5 hover:from-primary/10 hover:to-secondary/10 transition-all duration-300 cursor-pointer group"
                whileHover={{ scale: 1.05, y: -5 }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <motion.div
                  className="text-3xl font-medium text-gray-800 mb-2 group-hover:text-primary transition-colors duration-300"
                  whileHover={{ scale: 1.1 }}
                >
                  247K
                </motion.div>
                <div className="text-gray-600 font-medium group-hover:text-gray-800 transition-colors duration-300">
                  Served Breakfast
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div> */}
      </div>

      {/* Video Popup Modal */}
      {isVideoOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="relative w-full max-w-4xl bg-white rounded-lg overflow-hidden">
            {/* Close Button */}
            <button
              onClick={handleCloseVideo}
              className="absolute top-4 right-4 z-10 bg-black bg-opacity-50 text-white rounded-full p-2 hover:bg-opacity-75 transition-all"
            >
              <X size={24} />
            </button>

            {/* YouTube Video Embed */}
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                className="absolute top-0 left-0 w-full h-full"
                src={`https://www.youtube.com/embed/${currentVideoId}?autoplay=1&rel=0`}
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
